import { Button, Group, Select, SelectProps, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import { BankLogo } from 'src/Components/Shared/BankLogo';
import { FRENCH_BANKS, OTHER_BANK_VALUE } from 'src/Constants/frenchBanks';

import styles from './BankAccountForm.module.scss';
import { Props } from './BankAccountForm.types';

const BankAccountForm = ({ form, onSubmit, onCancel, submitButtonLabel }: Props) => {
  const { t } = useTranslation();

  const bankOptions = [
    ...FRENCH_BANKS.map(bank => ({ value: bank.name, label: bank.name })),
    { value: OTHER_BANK_VALUE, label: t('bankAccounts.form.otherBank', 'Autre banque') },
  ];

  const renderBankOption: SelectProps['renderOption'] = ({ option }) => {
    const bank = FRENCH_BANKS.find(b => b.name === option.value);
    return (
      <Group gap="sm">
        <BankLogo domain={bank?.domain} name={bank?.name ?? option.label} size="sm" />
        <span>{option.label}</span>
      </Group>
    );
  };

  const handleBankSelectionChange = (value: string | null) => {
    const bank = FRENCH_BANKS.find(b => b.name === value);
    form.setFieldValue('bankSelection', value ?? '');
    form.setFieldValue('bankName', bank?.name ?? '');
  };

  return (
    <form id="bank-account-form" onSubmit={onSubmit} className={styles.container}>
      <TextInput
        label={t('bankAccounts.form.nameLabel', 'Nom du compte')}
        placeholder={t('bankAccounts.form.namePlaceholder', 'Ex: Compte commun LCL')}
        {...form.getInputProps('name')}
        withAsterisk
      />

      <Select
        label={t('bankAccounts.form.bankLabel', 'Banque')}
        placeholder={t('bankAccounts.form.bankPlaceholder', 'Sélectionner une banque')}
        data={bankOptions}
        renderOption={renderBankOption}
        searchable
        clearable
        value={form.values.bankSelection || null}
        onChange={handleBankSelectionChange}
      />

      {form.values.bankSelection === OTHER_BANK_VALUE && (
        <TextInput
          label={t('bankAccounts.form.otherBankNameLabel', 'Nom de la banque')}
          {...form.getInputProps('bankName')}
        />
      )}

      <TextInput
        label={t('bankAccounts.form.bankDetailLabel', 'Caisse régionale / agence')}
        placeholder={t('bankAccounts.form.bankDetailPlaceholder', 'Ex: Île-de-France')}
        {...form.getInputProps('bankDetail')}
      />

      <div className={styles.sectionTitle}>
        {t('bankAccounts.form.coordinatesTitle', 'Coordonnées pour les virements')}
      </div>

      <TextInput
        label={t('bankAccounts.form.holderNameLabel', 'Bénéficiaire')}
        placeholder={t('bankAccounts.form.holderNamePlaceholder', 'Ex : Indivision Rochette')}
        {...form.getInputProps('holderName')}
      />

      <TextInput
        label={t('bankAccounts.form.ibanLabel', 'IBAN')}
        placeholder="FR76 3000 6000 0112 3456 7890 189"
        autoComplete="off"
        {...form.getInputProps('iban')}
      />

      <TextInput
        label={t('bankAccounts.form.bicLabel', 'BIC')}
        placeholder="AGRIFRPP"
        autoComplete="off"
        {...form.getInputProps('bic')}
      />

      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('common.cancel', 'Annuler')}
        </Button>
        <Button type="submit" variant="primary">
          {submitButtonLabel}
        </Button>
      </div>
    </form>
  );
};

export default BankAccountForm;
